// src/components/SurgeryKaikeiPanel.tsx (2026-07-02 新設・ベータ)
// 手術会計明細ドリル。手術(sno50)+麻酔(sno54)を 会計明細区分(手術料501/特定材料092/麻酔541/
//   術中薬剤301/輸血511)× 台帳分/台帳外 × 診療科 × 個別tencd に分解(金額完全保存)。
//   診療統計明細リバエン(RP明細recon)× 手術台帳(ope_sera)突合由来。202404 実測。
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { SurgeryKaikeiRow } from "@shared/api";
import { useSort, SortTh } from "../lib/useSort";

// depcd → 診療科名(EMRミラーに科名マスタ未populate=既知分のみ手当。他はコード表示)
const DEPT_NAME: Record<string, string> = { "0003": "循環器", "0004": "消化器", "0007": "外科系", "0001": "内科系" };
const deptName = (cd: string) => DEPT_NAME[cd] ? `${DEPT_NAME[cd]}(${cd})` : cd;

const CAT_ORDER = ["手術料(501)", "特定材料(092)", "麻酔料(541)", "術中薬剤(301)", "輸血(511)", "手術_付随", "麻酔_付随"];
const mm = (v: number) => (v / 1e6).toFixed(1);   // 百万円

// サマリ行の型(会計区分 × 台帳分/台帳外/合計)。SortTh の get キャスト用。
type SummaryRow = { cat: string; 台帳分: number; 台帳外: number; 合計: number };

export function SurgeryKaikeiPanel() {
  const { data, isFetching } = useQuery({ queryKey: ["surgery_kaikei"], queryFn: api.surgeryKaikei, staleTime: 60_000 });
  const rows: SurgeryKaikeiRow[] = data?.rows ?? [];
  const [focusCat, setFocusCat] = useState<string | null>(null);
  const [daichoFilter, setDaichoFilter] = useState<"all" | "台帳分" | "台帳外">("all");

  // サマリ: 会計区分 × 台帳
  const summary = useMemo(() => {
    const m: Record<string, { 台帳分: number; 台帳外: number }> = {};
    for (const r of rows) { (m[r.会計区分] ??= { 台帳分: 0, 台帳外: 0 }); m[r.会計区分][r.台帳 as "台帳分" | "台帳外"] += r.金額; }
    return CAT_ORDER.filter((c) => m[c]).map((c) => ({ cat: c, ...m[c], 合計: m[c].台帳分 + m[c].台帳外 }));
  }, [rows]);
  const total = useMemo(() => summary.reduce((a, s) => ({ 台帳分: a.台帳分 + s.台帳分, 台帳外: a.台帳外 + s.台帳外, 合計: a.合計 + s.合計 }), { 台帳分: 0, 台帳外: 0, 合計: 0 }), [summary]);

  // ドリル: focusCat の 診療科×tencd(台帳フィルタ)
  const drill = useMemo(() => {
    if (!focusCat) return [];
    const f = rows.filter((r) => r.会計区分 === focusCat && (daichoFilter === "all" || r.台帳 === daichoFilter));
    return f.slice().sort((a, b) => b.金額 - a.金額).slice(0, 40);
  }, [rows, focusCat, daichoFilter]);

  // クリックソート(既定は上記の固定順=CAT_ORDER / 金額降順を維持)。合計行は表下部の別 <tr> なので pin 不要。
  const ss = useSort(summary);
  const ds = useSort(drill);

  return (
    <div>
      <section className="card p-3 mb-3">
        <h2 className="text-sm font-bold mb-1">🔪 手術会計明細ドリル（手術を会計区分 × 台帳分/台帳外に分解）
          <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">202404 実測</span>
        </h2>
        <p className="text-[11px] text-slate-500 mb-2">
          診療統計明細リバエン(RP明細)× 手術台帳(ope_sera)突合・金額完全保存。手術50+麻酔54 = {mm(total.合計)}百万円/月。
          {isFetching && <span className="text-slate-400 ml-2">更新中…</span>}
        </p>
        <table className="text-[12px] border-collapse w-full">
          <thead className="bg-slate-50"><tr>
            <SortTh s={ss} sortKey="cat" get={(r) => (r as SummaryRow).cat} className="px-2 py-1 text-left border-b">会計区分</SortTh>
            <SortTh s={ss} sortKey="daichoBun" get={(r) => (r as SummaryRow).台帳分} className="px-2 py-1 text-right border-b">台帳分</SortTh>
            <SortTh s={ss} sortKey="daichoGai" get={(r) => (r as SummaryRow).台帳外} className="px-2 py-1 text-right border-b">台帳外</SortTh>
            <SortTh s={ss} sortKey="goukei" get={(r) => (r as SummaryRow).合計} className="px-2 py-1 text-right border-b">合計(百万円)</SortTh>
            <SortTh s={ss} sortKey="daichoRate" get={(r) => { const x = r as SummaryRow; return x.合計 > 0 ? x.台帳分 / x.合計 : 0; }} className="px-2 py-1 text-right border-b">台帳分率</SortTh>
          </tr></thead>
          <tbody>
            {ss.rows.map((s) => (
              <tr key={s.cat} className={`hover:bg-blue-50 cursor-pointer ${focusCat === s.cat ? "bg-amber-50" : ""}`}
                  onClick={() => setFocusCat(focusCat === s.cat ? null : s.cat)}>
                <td className="px-2 py-1 text-left border-b">{s.cat} <span className="text-slate-400 text-[10px]">▶</span></td>
                <td className="px-2 py-1 text-right border-b tabular-nums">{mm(s.台帳分)}</td>
                <td className="px-2 py-1 text-right border-b tabular-nums text-orange-700">{mm(s.台帳外)}</td>
                <td className="px-2 py-1 text-right border-b tabular-nums font-bold">{mm(s.合計)}</td>
                <td className="px-2 py-1 text-right border-b tabular-nums">{s.合計 > 0 ? (100 * s.台帳分 / s.合計).toFixed(1) : "0"}%</td>
              </tr>
            ))}
            <tr className="bg-slate-100 font-bold"><td className="px-2 py-1 border-b">合計</td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{mm(total.台帳分)}</td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{mm(total.台帳外)}</td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{mm(total.合計)}</td>
              <td className="px-2 py-1 text-right border-b tabular-nums">{total.合計 > 0 ? (100 * total.台帳分 / total.合計).toFixed(1) : "0"}%</td></tr>
          </tbody>
        </table>
        <p className="text-[10px] text-slate-400 mt-2">
          ※台帳外=手術台帳に載らない手術(PCI循環器・内視鏡消化器・外来処置等)。手術料の台帳分率≈73%は20260627検証と一致。
        </p>
      </section>

      {focusCat && (
        <section className="card p-3">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="text-sm font-bold">🎯 {focusCat} の内訳(診療科 × 個別手術tencd・上位40・百万円)</h3>
            <div className="flex gap-1 text-xs ml-auto">
              {(["all", "台帳分", "台帳外"] as const).map((d) => (
                <button key={d} onClick={() => setDaichoFilter(d)}
                  className={`px-2 py-0.5 rounded ${daichoFilter === d ? "bg-blue-600 text-white" : "bg-slate-100 hover:bg-slate-200"}`}>
                  {d === "all" ? "全て" : d}</button>
              ))}
            </div>
            <button onClick={() => setFocusCat(null)} className="text-xs px-2 py-0.5 bg-amber-100 rounded hover:bg-amber-200">✕ 閉じる</button>
          </div>
          <div className="overflow-auto" style={{ maxHeight: "55vh" }}>
            <table className="text-[11px] border-collapse w-full">
              <thead className="sticky top-0 bg-slate-50"><tr>
                <SortTh s={ds} sortKey="dept" get={(r) => (r as SurgeryKaikeiRow).診療科cd} className="px-2 py-1 text-left border-b">診療科</SortTh>
                <SortTh s={ds} sortKey="daicho" get={(r) => (r as SurgeryKaikeiRow).台帳} className="px-2 py-1 text-left border-b">台帳</SortTh>
                <SortTh s={ds} sortKey="tencd" get={(r) => (r as SurgeryKaikeiRow).tencd} className="px-2 py-1 text-left border-b">tencd</SortTh>
                <SortTh s={ds} sortKey="name" get={(r) => (r as SurgeryKaikeiRow).名称} className="px-2 py-1 text-left border-b">名称</SortTh>
                <SortTh s={ds} sortKey="cnt" get={(r) => (r as SurgeryKaikeiRow).件数} className="px-2 py-1 text-right border-b">件数</SortTh>
                <SortTh s={ds} sortKey="kingaku" get={(r) => (r as SurgeryKaikeiRow).金額} className="px-2 py-1 text-right border-b">金額(百万円)</SortTh>
              </tr></thead>
              <tbody>
                {ds.rows.map((r, i) => (
                  <tr key={i} className="hover:bg-blue-50">
                    <td className="px-2 py-1 text-left border-b whitespace-nowrap">{deptName(r.診療科cd)}</td>
                    <td className={`px-2 py-1 text-left border-b ${r.台帳 === "台帳外" ? "text-orange-700" : ""}`}>{r.台帳}</td>
                    <td className="px-2 py-1 text-left border-b font-mono text-[10px]">{r.tencd}</td>
                    <td className="px-2 py-1 text-left border-b">{r.名称}</td>
                    <td className="px-2 py-1 text-right border-b tabular-nums">{r.件数.toLocaleString()}</td>
                    <td className="px-2 py-1 text-right border-b tabular-nums">{mm(r.金額)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
